import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, uploadImage } from "../utils/api";
import { getSocket } from "../utils/socket";
import { useApp } from "../context/AppContext";
import { formatScore } from "../utils/format";
import { Avatar, vibeRingKind } from "../components/Avatar";
import { LockIcon } from "../components/messages/icons";
import { useIsMobile } from "../hooks/useIsMobile";

// "Chat" en premier, comme sur la fiche communauté (src/pages/Community.tsx) — un rituel n'avait
// jusqu'ici aucune discussion de groupe (même pas créée côté serveur, voir POST /rituals).
const TABS = ["Chat", "Infos", "Participants"];
const JOURS_LABELS: Record<string, string> = { Lun: "lundi", Mar: "mardi", Mer: "mercredi", Jeu: "jeudi", Ven: "vendredi", Sam: "samedi", Dim: "dimanche" };
const FREQUENCY_LABELS: Record<string, string> = { daily: "Chaque jour", weekly: "Chaque semaine", biweekly: "Toutes les 2 semaines", monthly: "Chaque mois" };

const PALETTE = ["#0a0a0a", "#0a0a0a", "#0a0a0a", "#333333", "#666666", "#777777", "#4d4d4d"];
const colorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
};
const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Icône "cycle/répétition" — cohérente avec le badge utilisé ailleurs pour représenter un rituel.
const RepeatIcon = ({ size = 40, color = "white" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

export default function Ritual() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useApp();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState("Infos");
  const [ritual, setRitual] = useState<any>(null);
  const [participants, setParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingInfo, setSavingInfo] = useState(false);
  const [deletingRitual, setDeletingRitual] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [inputText, setInputText] = useState("");

  const isMember = ritual?.my_role != null;
  const isAdmin = ritual?.my_role === "admin";

  const loadRitual = () => {
    if (!id) return;
    api.get(`/rituals/${id}`).then(data => {
      if (!data?.ritual) { setNotFound(true); return; }
      setRitual(data.ritual);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  };

  useEffect(() => { loadRitual(); }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/rituals/${id}/participants`).then(data => setParticipants(data?.participants || [])).catch(() => {});
  }, [id]);

  // Demandes de participation en attente (rituel "prive", ou "amis" refusé faute d'amitié — voir
  // POST /:id/join côté serveur) — visibles seulement par un admin.
  const [pending, setPending] = useState<any[]>([]);
  useEffect(() => {
    if (!id || !isAdmin) return;
    api.get(`/rituals/${id}/participants/pending`).then(data => setPending(data?.requests || [])).catch(() => {});
  }, [id, isAdmin]);

  const respondPending = (userId: string, approve: boolean) => {
    const path = approve ? `/rituals/${id}/participants/${userId}/approve` : `/rituals/${id}/participants/${userId}`;
    const call = approve ? api.put(path, {}) : api.delete(path);
    call.then(() => {
      setPending((prev: any[]) => prev.filter(p => p.id !== userId));
      if (approve) {
        loadRitual();
        api.get(`/rituals/${id}/participants`).then(data => setParticipants(data?.participants || [])).catch(() => {});
      }
    }).catch(() => {});
  };

  // Chat de groupe — port fidèle de Community.tsx (même socket, même endpoint /messages/:id).
  useEffect(() => {
    if (!ritual?.conversation_id || !token) return;
    const socket = getSocket(token);
    socket.emit("join_conversation", ritual.conversation_id);
    socket.emit("mark_read", ritual.conversation_id);
    api.get(`/messages/${ritual.conversation_id}`).then(data => {
      if (data && Array.isArray(data.messages)) setMessages(data.messages);
    }).catch(() => {});

    const onNewMessage = ({ conversationId, message }: any) => {
      if (conversationId === ritual.conversation_id) setMessages(prev => [...prev, message]);
    };
    socket.on("new_message", onNewMessage);
    return () => { socket.off("new_message", onNewMessage); };
  }, [ritual?.conversation_id, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = () => {
    if (!inputText.trim() || !ritual?.conversation_id || !token) return;
    getSocket(token).emit("send_message", { conversationId: ritual.conversation_id, content: inputText.trim() });
    setInputText("");
  };

  const joinRitual = () => {
    if (!id) return;
    api.post(`/rituals/${id}/join`, {}).then(() => { loadRitual(); api.get(`/rituals/${id}/participants`).then(data => setParticipants(data?.participants || [])).catch(() => {}); }).catch(() => {});
  };

  const leaveRitual = () => {
    if (!id) return;
    if (!window.confirm("Quitter ce rituel ?")) return;
    api.post(`/rituals/${id}/leave`, {}).then(data => {
      if (data?.code === "last_admin") alert(data.error || "Désignez un autre administrateur avant de quitter le rituel.");
      else navigate("/profile?tab=Rituels");
    }).catch(() => alert("Erreur de connexion"));
  };

  // Exclure un participant / supprimer le rituel — manquaient totalement ici (seule l'édition
  // des infos existait déjà) contrairement à Community.tsx, qui a déjà removeMember/deleteCommunity.
  const removeParticipant = (participantId: string, participantName: string) => {
    if (!id) return;
    if (!window.confirm(`Exclure ${participantName} du rituel ?`)) return;
    api.delete(`/rituals/${id}/participants/${participantId}`)
      .then(data => {
        if (data?.error) { alert(data.error); return; }
        setParticipants(prev => prev.filter(p => p.id !== participantId));
        setRitual((r: any) => ({ ...r, members_count: Math.max(0, (r.members_count || 1) - 1) }));
      })
      .catch(() => alert("Erreur de connexion"));
  };

  const deleteRitual = () => {
    if (!id || !isAdmin) return;
    if (!window.confirm("Supprimer ce rituel ? Action irréversible, tous les participants perdront l'accès à son contenu et à sa discussion.")) return;
    setDeletingRitual(true);
    api.delete(`/rituals/${id}`).then(data => {
      if (data?.error) { alert(data.error); setDeletingRitual(false); return; }
      navigate("/profile?tab=Rituels");
    }).catch(() => { alert("Erreur de connexion"); setDeletingRitual(false); });
  };

  const startEditingInfo = () => {
    setEditTitle(ritual?.title || "");
    setEditCategory(ritual?.category || "");
    setEditDescription(ritual?.description || "");
    setEditingInfo(true);
  };

  const saveRitualInfo = () => {
    if (!id) return;
    setSavingInfo(true);
    api.put(`/rituals/${id}`, { title: editTitle, category: editCategory, description: editDescription })
      .then(data => {
        if (data?.ritual) { setRitual(data.ritual); setEditingInfo(false); }
        else alert(data?.error || "Erreur lors de la mise à jour");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSavingInfo(false));
  };

  // Changer la photo d'un rituel déjà créé — le cover_url existe en base depuis longtemps
  // (renseignable à la création sur mobile, même obligatoire là-bas) mais cette page l'ignorait
  // jusqu'ici complètement, retombant toujours sur le même dégradé uni. PUT /rituals/:id accepte
  // désormais cover_url (whitelisté côté serveur, ajouté au même moment que cette fonctionnalité).
  const changeCoverPhoto = async (file: File) => {
    if (!id || !isAdmin) return;
    setUploadingCover(true);
    try {
      const url = await uploadImage(file, "ritual");
      const data = await api.put(`/rituals/${id}`, { cover_url: url });
      if (data?.ritual) setRitual(data.ritual);
      else alert("Erreur lors de la mise à jour");
    } catch {
      alert("Erreur de connexion");
    }
    setUploadingCover(false);
  };

  const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, padding: 20, marginBottom: 12, border: "1px solid #F0F0F0" };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Chargement...</div>;
  if (notFound || !ritual) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Rituel introuvable.</div>;

  const schedule = [
    FREQUENCY_LABELS[ritual.frequency] || ritual.frequency,
    ritual.days && ritual.days.length > 0 ? ritual.days.map((d: string) => JOURS_LABELS[d] || d).join(", ") : null,
    ritual.time_of_day ? String(ritual.time_of_day).slice(0, 5) : null,
  ].filter(Boolean).join(" · ");

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "calc(100vh - 60px)", overflow: isMobile ? "visible" : "hidden" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* COVER — photo si le rituel en a une (cover_url, renseignable/obligatoire à la
            création), sinon le même dégradé uni qu'avant. */}
        <div style={{ height: 180, background: ritual.cover_url ? `url(${ritual.cover_url}) center/cover` : "linear-gradient(135deg, #555555 0%, #1a1a1a 100%)", position: "relative", flexShrink: 0, padding: 20, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {!!ritual.cover_url && <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.35)" }} />}
          {isAdmin && (
            <label style={{ position: "absolute", inset: 0, cursor: uploadingCover ? "default" : "pointer" }}>
              <input type="file" accept="image/*" disabled={uploadingCover} style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) changeCoverPhoto(f); e.target.value = ""; }} />
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }}
                onMouseOver={e => (e.currentTarget.style.opacity = "1")}
                onMouseOut={e => (e.currentTarget.style.opacity = "0")}>
                <div style={{ backgroundColor: "rgba(0,0,0,0.6)", borderRadius: 20, padding: "8px 16px" }}>
                  <span style={{ color: "white", fontSize: 13, fontWeight: 600 }}>{uploadingCover ? "Envoi..." : "Changer la photo"}</span>
                </div>
              </div>
            </label>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <RepeatIcon size={22} />
            </div>
            {ritual.streak > 0 && (
              <div style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "5px 12px", border: "0.5px solid rgba(255,255,255,0.4)" }}>
                <span style={{ color: "white", fontSize: 12, fontWeight: 700 }}>🔥 {ritual.streak} sem.</span>
              </div>
            )}
          </div>
          <div>
            {ritual.category && (
              <div style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "4px 12px", border: "0.5px solid rgba(255,255,255,0.4)", display: "inline-block", marginBottom: 8 }}>
                <span style={{ color: "white", fontSize: 11, fontWeight: 600 }}>{ritual.category}</span>
              </div>
            )}
            <div style={{ color: "white", fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{ritual.title}</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{schedule}{ritual.location_name ? ` · ${ritual.location_name}` : ""}</div>
          </div>

          {!isMember && (
            <button onClick={joinRitual} style={{ position: "absolute", bottom: 16, right: 20, display: "flex", alignItems: "center", gap: 6, backgroundColor: "white", color: "#0a0a0a", border: "none", borderRadius: 20, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              Rejoindre
            </button>
          )}
        </div>

        {/* TABS */}
        <div style={{ display: "flex", backgroundColor: "white", borderBottom: "1px solid #E0E0E0", flexShrink: 0 }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flex: 1, padding: "13px 8px", border: "none", cursor: "pointer",
              backgroundColor: "transparent",
              borderBottom: activeTab === tab ? "2px solid #0a0a0a" : "2px solid transparent",
              color: activeTab === tab ? "#0a0a0a" : "#999",
              fontWeight: activeTab === tab ? 700 : 400, fontSize: 13,
            }}>{tab}</button>
          ))}
        </div>

        {/* CONTENU */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

          {activeTab === "Chat" && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {!isMember ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 32 }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LockIcon size={24} color="#999" />
                  </div>
                  <p style={{ fontSize: 15, color: "#999", textAlign: "center" }}>Rejoignez le rituel pour accéder au chat</p>
                  <button onClick={joinRitual} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "12px 28px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Rejoindre</button>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
                    {messages.length === 0 && <div style={{ textAlign: "center", color: "#999", fontSize: 13, marginTop: 40 }}>Aucun message pour le moment</div>}
                    {messages.map((msg, index) => {
                      const isMe = msg.user_id === user?.id;
                      const showAvatar = !isMe && (index === 0 || messages[index - 1].user_id !== msg.user_id);
                      return (
                        <div key={msg.id} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: 6, alignItems: "flex-end", gap: 8 }}>
                          {!isMe && (
                            <div style={{ width: 32, flexShrink: 0 }}>
                              {showAvatar && (
                                <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: colorFromId(msg.user_id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 700 }}>{initials(msg.user_name)}</div>
                              )}
                            </div>
                          )}
                          <div style={{ maxWidth: "60%", display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
                            {showAvatar && !isMe && <span style={{ fontSize: 11, color: "#999", fontWeight: 600, marginBottom: 3, marginLeft: 4 }}>{msg.user_name}</span>}
                            <div style={{ backgroundColor: isMe ? "#0a0a0a" : "white", color: isMe ? "white" : "#0a0a0a", borderRadius: 18, borderBottomRightRadius: isMe ? 4 : 18, borderBottomLeftRadius: isMe ? 18 : 4, padding: "9px 14px", fontSize: 14, lineHeight: 1.5, border: isMe ? "none" : "1px solid #F0F0F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                              {msg.content}
                            </div>
                            <span style={{ fontSize: 10, color: "#bbb", marginTop: 3, marginLeft: 4, marginRight: 4 }}>{new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderTop: "1px solid #F0F0F0", backgroundColor: "white" }}>
                    <input value={inputText} onChange={e => setInputText(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} placeholder="Message..." style={{ flex: 1, padding: "10px 16px", borderRadius: 24, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", backgroundColor: "#F8F8F8" }} />
                    <button onClick={sendMessage} disabled={!inputText.trim()} style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: inputText.trim() ? "#0a0a0a" : "#E0E0E0", border: "none", cursor: inputText.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 18, fontWeight: 700, flexShrink: 0 }}>
                      →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "Infos" && (
            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>À propos</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    {isAdmin && (
                      <button onClick={() => navigate(`/create?ritual_id=${id}&ritual_name=${encodeURIComponent(ritual.title)}`)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>+ Créer une publication</button>
                    )}
                    {isAdmin && !editingInfo && (
                      <button onClick={startEditingInfo} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>Gérer</button>
                    )}
                  </div>
                </div>
                {editingInfo ? (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase" }}>Titre</label>
                    <input value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "#F8F8F8", boxSizing: "border-box" }} />
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase" }}>Catégorie</label>
                    <input value={editCategory} onChange={e => setEditCategory(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "#F8F8F8", boxSizing: "border-box" }} />
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase" }}>Description</label>
                    <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "#F8F8F8", resize: "none" as const, boxSizing: "border-box" }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setEditingInfo(false)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Annuler</button>
                      <button onClick={saveRitualInfo} disabled={savingInfo || !editTitle.trim()} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: savingInfo || !editTitle.trim() ? 0.6 : 1 }}>{savingInfo ? "..." : "Enregistrer"}</button>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.8, margin: 0 }}>{ritual.description || "Aucune description."}</p>
                )}
              </div>

              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Informations</div>
                {[
                  schedule,
                  ritual.location_name,
                  `Rituel ${ritual.audience === "public" ? "public" : "sur invitation"}`,
                  `${ritual.members_count || 0} participant${Number(ritual.members_count) > 1 ? "s" : ""}`,
                  `Créé par ${ritual.author_name}`,
                  ...(ritual.rules && ritual.rules.length > 0 ? [`Règles : ${ritual.rules.join(", ")}`] : []),
                ].filter(Boolean).map((text, i) => (
                  <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                    <span style={{ fontSize: 13 }}>{text}</span>
                  </div>
                ))}
              </div>

              {isMember && !isAdmin && (
                <button onClick={leaveRitual} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "0.5px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: "pointer", fontSize: 14, color: "#0a0a0a", fontWeight: 600 }}>
                  Quitter le rituel
                </button>
              )}

              {isAdmin && (
                <button onClick={deleteRitual} disabled={deletingRitual} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "1.5px solid #c0392b", backgroundColor: "transparent", cursor: "pointer", fontSize: 14, color: "#c0392b", fontWeight: 600, marginTop: 10, opacity: deletingRitual ? 0.6 : 1 }}>
                  {deletingRitual ? "..." : "Supprimer le rituel"}
                </button>
              )}
            </div>
          )}

          {activeTab === "Participants" && (
            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              {isAdmin && pending.length > 0 && (
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Demandes en attente ({pending.length})</div>
                  {pending.map((p: any) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                      <Avatar id={p.id} name={p.name} uri={p.avatar_url} size={36} />
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                      <button onClick={() => respondPending(p.id, false)} style={{ border: "none", background: "#FDEAEA", color: "#C0392B", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Refuser</button>
                      <button onClick={() => respondPending(p.id, true)} style={{ border: "none", background: "#0a0a0a", color: "white", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Accepter</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{ritual.members_count || 0} participant{Number(ritual.members_count) > 1 ? "s" : ""}</div>
                {participants.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>Aucun participant pour le moment</div>}
                {participants.map(p => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #F5F5F5" }}>
                    <div onClick={() => navigate("/profile/" + p.id)} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, cursor: "pointer" }}>
                      <Avatar id={p.id} name={p.name} uri={p.avatar_url} size={44} ringKind={vibeRingKind(p.has_real, p.has_active)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                          {p.role === "admin" && <span style={{ backgroundColor: "#F0F0F0", color: "#333333", fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "2px 7px" }}>Créateur</span>}
                        </div>
                        <div style={{ fontSize: 12, color: "#0a0a0a" }}>★ {formatScore(p.score)} / 10</div>
                      </div>
                    </div>
                    {isAdmin && p.id !== user?.id && (
                      <button onClick={() => removeParticipant(p.id, p.name)} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", color: "#666", cursor: "pointer", flexShrink: 0 }}>Exclure</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SIDEBAR */}
      <div style={{ display: isMobile ? "none" : "block", width: 280, flexShrink: 0, overflowY: "auto", padding: "20px 20px 20px 0", borderLeft: "1px solid #F0F0F0" }}>
        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, marginBottom: 12, border: "1px solid #F0F0F0" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Participants ({participants.length})</div>
          {participants.slice(0, 8).map(p => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #F8F8F8", cursor: "pointer" }} onClick={() => navigate("/profile/" + p.id)}>
              <Avatar id={p.id} name={p.name} uri={p.avatar_url} size={36} ringKind={vibeRingKind(p.has_real, p.has_active)} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, border: "1px solid #F0F0F0" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>À propos</div>
          <div style={{ fontSize: 13, color: "#666", lineHeight: 1.7, marginBottom: 12 }}>{(ritual.description || "").slice(0, 100)}{ritual.description?.length > 100 ? "..." : ""}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: "#0a0a0a" }}>{ritual.members_count || 0}</span>
            <span style={{ fontSize: 12, color: "#999" }}>participants</span>
          </div>
          {!isMember && (
            <button onClick={joinRitual} style={{ width: "100%", backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 12, padding: "10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              Rejoindre
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
